// Quick Add compatibility helpers used by the custom collection cards.
if (typeof window.HTMLUpdateUtility === 'undefined') {
  window.HTMLUpdateUtility = class HTMLUpdateUtility {
    static setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode?.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  };
}

if (typeof window.ModalDialog === 'undefined') {
  window.ModalDialog = class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide());
      this.addEventListener('keyup', (event) => {
        if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      if (typeof trapFocus === 'function') {
        trapFocus(this, this.querySelector('[role="dialog"]') || this);
      }
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
    }
  };
}

if (!customElements.get('modal-opener')) {
  customElements.define(
    'modal-opener',
    class ModalOpener extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-product-url]');
          if (!button || !this.contains(button)) return;

          event.preventDefault();
          const selector = this.getAttribute('data-modal');
          const modal = selector ? document.querySelector(selector) : null;
          if (modal && typeof modal.show === 'function') modal.show(button);
        });
      }
    }
  );
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends window.ModalDialog {
      constructor() {
        super();
        this.modalContent = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification && typeof cartNotification.setActiveElement === 'function') {
          cartNotification.setActiveElement(this.openedBy);
        }
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        if (!this.modalContent) {
          console.error('[QuickAdd] Modal content container not found');
          return;
        }

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        // Classify first so Readymade can never fall through to single-variant add.
        this.fetchProductData(productUrl)
          .then(async (productData) => {
            if (this.isReadymadeProduct(productData)) {
              window.location.assign(productUrl);
              return;
            }

            const response = await fetch(productUrl);
            if (!response.ok) throw new Error('Quick add product page could not be loaded');

            const responseText = await response.text();
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Quick add product content not found');

            const hasVariantSelector = Boolean(productElement.querySelector('variant-selects'));
            if (!hasVariantSelector) {
              await this.addSingleVariantToCart(productElement, opener);
              return;
            }

            window.location.assign(productUrl);
          })
          .catch((error) => {
            console.error('[QuickAdd] Unable to complete product-card action:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      productJsonUrl(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        url.search = '';
        url.hash = '';
        url.pathname = url.pathname.replace(/\/$/, '') + '.js';
        return url.toString();
      }

      async fetchProductData(productUrl) {
        const response = await fetch(this.productJsonUrl(productUrl), {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Quick add product data could not be loaded');
        return response.json();
      }

      isReadymadeProduct(productData) {
        const productType = String(productData?.type || '').trim().toLowerCase();
        const tags = Array.isArray(productData?.tags)
          ? productData.tags
          : String(productData?.tags || '').split(',');

        return (
          productType.includes('readymade') ||
          tags.some((tag) => String(tag).trim().toLowerCase().includes('readymade'))
        );
      }

      async showReadymadeSelector(productUrl, opener, productElement) {
        const sectionUrl = new URL(productUrl, window.location.origin);
        sectionUrl.searchParams.set('section_id', 'readymade-size-selector');

        const response = await fetch(sectionUrl.toString(), {
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Readymade size selector could not be loaded');

        const sectionHtml = await response.text();
        if (!sectionHtml.trim()) throw new Error('Readymade size selector returned empty content');

        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) throw new Error('Readymade product info container not found');

        // Readymade modal only: remove the generic single-size controls so there is one clean flow.
        infoContainer.querySelector('variant-selects')?.remove();
        infoContainer.querySelector('[id^="Quantity-Form-"]')?.remove();
        infoContainer.querySelectorAll('product-form').forEach((form) => form.remove());
        infoContainer.querySelector('.product__view-details')?.remove();

        const selectorHost = productElement.ownerDocument.createElement('div');
        selectorHost.className = 'readymade-size-selector-host';
        selectorHost.innerHTML = sectionHtml;
        infoContainer.appendChild(selectorHost);

        // Keep the canonical selector inside the native product-info context, matching
        // the previously working Readymade implementation instead of floating it alone.
        this.preprocessHTML(productElement);
        window.HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (!this.modalContent.querySelector('.size-order-grid')) {
          throw new Error('Readymade size selector failed to render');
        }

        super.show(opener);
      }

      async addSingleVariantToCart(productElement, opener) {
        const variantInput = productElement.querySelector('.product-variant-id, input[name="id"]');
        const quantityInput = productElement.querySelector('input[name="quantity"]');
        const variantId = parseInt(variantInput?.value || '0', 10);
        const quantity = Math.max(
          1,
          parseInt(
            quantityInput?.dataset.setQuantity ||
            quantityInput?.dataset.setQty ||
            quantityInput?.step ||
            quantityInput?.dataset.min ||
            quantityInput?.value ||
            '1',
            10
          ) || 1
        );

        if (!variantId) throw new Error('Quick add variant ID not found');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = {
          items: [{ id: variantId, quantity }]
        };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'Unable to add product to cart');
        }

        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(opener);
          cart.renderContents(state);
        } else {
          window.location.assign('/cart');
          return;
        }

        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error('Unable to read updated cart');
        const updatedCart = await cartResponse.json();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quick-add',
            productVariantId: String(variantId),
            cartData: updatedCart
          });
        } else if (typeof window.refreshProductCardCartQuantities === 'function') {
          window.refreshProductCardCartQuantities();
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        if (!sectionId) return;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
